import { observabilityAppService } from '@ctct/myaccount-utils-static';

import { billingInfoFormEventBus } from './BillingInfoFormEventBus';
import { BillingInfoFormService } from './services/BillingInfoFormService';
import { DataPreparationService } from './services/DataPreparationService';
import { FormDefaultValuesService } from './services/FormDefaultValuesService';
import { FormMountService } from './services/FormMountService';
import type { BillingInfoFormValues, Init } from './types';
import type { EventBus } from '../../helpers';
import { managerHelpers } from '../../helpers';
import type { MergedApiReturnType, MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import { FormServiceFactory } from '../_shared/Form/FormServiceFactory';
import type { AllFormServices, BaseFormEvents } from '../_shared/Form/types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

type AllServices = [
  ...AllFormServices<BillingInfoFormValues>,
  BillingInfoFormService,
  DataPreparationService,
  FormMountService
];

export type BillingInfoFormData = MergedDataReturnType<AllServices>;

export type BillingInfoFormHandlers = MergedHandlersReturnType<AllServices>;

type TApi = MergedApiReturnType<AllServices>;

const performanceTracker = observabilityAppService.createPerformanceTracker('billingInfoFormInit', {
  tags: ['application-layer', 'BillingInfoFormManager'],
});

export class BillingInfoFormManager extends WithApi<TApi> {
  public billingInfoFormInit = ({ pageStore, queries, t, flags = {} }: Init) => {
    const [countriesQuery, billingInfoQuery, billingAccountQuery, customerInfoQuery] = queries;
    const pending =
      countriesQuery.isPending ||
      billingInfoQuery.isPending ||
      billingAccountQuery.isPending ||
      customerInfoQuery.isPending;
    const error =
      countriesQuery.error || billingInfoQuery.error || billingAccountQuery.error || customerInfoQuery.error;

    if (pending) {
      performanceTracker.reportLoading();

      return {
        status: 'pending',
        data: null,
        handlers: null,
      } as const;
    }

    if (error) {
      performanceTracker.reportError({
        error,
        meta: {
          'countriesQuery.error': !!countriesQuery.error,
          'billingInfoQuery.error': !!billingInfoQuery.error,
          'billingAccountQuery.error': !!billingAccountQuery.error,
          'customerInfoQuery.error': !!customerInfoQuery.error,
        },
      });

      return {
        status: 'error',
        data: error,
        handlers: {
          retry: () => {
            managerHelpers.retryQueries(queries);
          },
        },
      } as const;
    }

    const countries = FormServiceFactory.createCountriesService(countriesQuery.data, t);
    const translation = FormServiceFactory.createTranslationService(t);
    const validation = FormServiceFactory.createValidationService(countriesQuery.data, t);

    const uiService = FormServiceFactory.createUIService<BillingInfoFormValues>(
      () => pageStore.getState().billingInfoFormIsDisplayingCard,
      () => pageStore.getState().billingInfoFormIsDirty,
      (isDirty: boolean) => pageStore.setState({ billingInfoFormIsDirty: isDirty }),
      (isDisplayingCard: boolean) => pageStore.setState({ billingInfoFormIsDisplayingCard: isDisplayingCard }),
      (isLoading: boolean) => pageStore.setState({ billingInfoFormIsLoading: isLoading }),
      billingInfoFormEventBus as EventBus<BaseFormEvents<BillingInfoFormValues>>
    );

    const formChangeHandler = FormServiceFactory.createFormChangeHandlerService<BillingInfoFormValues>(
      countriesQuery.data,
      billingInfoFormEventBus as EventBus<BaseFormEvents<BillingInfoFormValues>>,
      (isDirty: boolean) => pageStore.setState({ billingInfoFormIsDirty: isDirty }),
      () => uiService.clearFormErrorsFn?.()
    );

    const billingInfoFormService = new BillingInfoFormService(
      pageStore,
      billingInfoQuery.data,
      uiService,
      validation,
      billingInfoFormEventBus
    );

    const formDefaultValuesService = new FormDefaultValuesService(
      billingInfoQuery.data,
      countriesQuery.data,
      customerInfoQuery.data,
      flags.skipFormPrefill ?? false,
      billingAccountQuery.data
    );

    const dataPreparationService = new DataPreparationService(
      pageStore,
      billingInfoQuery.data,
      billingAccountQuery.data,
      formDefaultValuesService.getDefaultValues,
      uiService
    );

    const formMountService = new FormMountService(
      pageStore,
      formDefaultValuesService.getDefaultValues,
      uiService,
      billingInfoFormService.validate,
      billingAccountQuery.data
    );

    this.initApi({
      ...countries.api(),
      ...translation.api(),
      ...validation.api(),
      ...uiService.api(),
      ...formChangeHandler.api(),
      ...billingInfoFormService.api(),
      ...formMountService.api(),
      ...dataPreparationService.api(),
      ...formDefaultValuesService.api(),
    });

    performanceTracker.reportLoaded();

    return {
      status: 'loaded',
      data: {
        ...countries.data(),
        ...translation.data(),
        ...validation.data(),
        ...uiService.data(),
        ...formChangeHandler.data(),
        ...billingInfoFormService.data(),
        ...formMountService.data(),
        ...dataPreparationService.data(),
        ...formDefaultValuesService.data(),
      },
      handlers: {
        ...countries.handlers(),
        ...translation.handlers(),
        ...validation.handlers(),
        ...uiService.handlers(),
        ...formChangeHandler.handlers(),
        ...billingInfoFormService.handlers(),
        ...formMountService.handlers(),
        ...dataPreparationService.handlers(),
        ...formDefaultValuesService.handlers(),
      },
      lifecycle: {
        onMount: () => {
          formMountService.initFormValues();
        },
        onUnmountOrUnload() {
          performanceTracker.dispose();
        },
      } satisfies WidgetLifecycle,
    } as const;
  };
}
